import { DateTime } from 'luxon';

import { TFormatStore } from '../../wrappers/StoreLocatorWrapper/StoreLocatorUtils/StoreLocatorUtils';
import { TStoreCards } from '../StoreCards/StoreCards';
import { TSelectedService } from '../StoreFinder';
import {
  CLICK_AND_COLLECT,
  BUY_CURRENCY_IN_STORE,
  ALL_TRAVEL_MONEY_STORES,
} from '../../../constants/listOptions';

type TStoreCoordinates = {
  lat: number;
  lng: number;
};

export type TClosestStores = (TFormatStore & {
  distance: number;
})[];

export const filterStores = (
  stores: TFormatStore[],
  service: TSelectedService
): TFormatStore[] => {
  const result = service
    ? stores.filter((store) => {
        const { isBureau, isClickAndCollect, isEurosAndUsDollarsToGo } = store;

        switch (service) {
          case CLICK_AND_COLLECT:
            return isClickAndCollect;
          case BUY_CURRENCY_IN_STORE:
            return isBureau;
          case ALL_TRAVEL_MONEY_STORES:
            return isClickAndCollect || isBureau || isEurosAndUsDollarsToGo;
          default:
            return false;
        }
      })
    : stores;

  return result;
};

const haversineDistance = (
  coords1: TStoreCoordinates,
  coords2: TStoreCoordinates
) => {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 6371; // Radius of the Earth in km

  const dLat = toRad(coords2.lat - coords1.lat);
  const dLon = toRad(coords2.lng - coords1.lng);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(coords1.lat)) *
      Math.cos(toRad(coords2.lat)) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const findClosestStores = (
  location: {
    lat: number;
    lng: number;
  },
  storesData: TFormatStore[]
): TClosestStores => {
  const sorted = storesData
    .map((store) => {
      return {
        ...store,
        distance: haversineDistance(location, {
          lat: store?.coordinates?.lat,
          lng: store?.coordinates?.lng,
        }),
      };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 6);

  return sorted;
};

export function formatToTwoDecimals(value: number | string): string {
  const number = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(number) || number === null || number === undefined) {
    console.warn('Invalid number provided to formatToTwoDecimals');
    return '0.00';
  }
  return number.toFixed(2);
}

const extractWeekdays = (obj: Record<string, any>) => {
  const weekdays = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => weekdays.includes(key.toLowerCase()))
  );
};

export function getStoreStatus(schedule: TStoreCards): string {
  const scheduleWithWeekdays = extractWeekdays(schedule);

  const now = DateTime.local().setZone('Europe/London'); // Current date and time
  const today = now.toFormat('cccc').toLowerCase(); // Get today's day name (e.g., "monday")
  const currentTime = now.toFormat('HH:mm'); // Current time in "HH:mm" format

  // Helper function to format time to "h:mma" (e.g., "8:00pm")
  const formatTime = (time: string) =>
    DateTime.fromFormat(time, 'HH:mm').toFormat('h:mma').toLowerCase();

  // Check if the store has a schedule for today
  if (scheduleWithWeekdays[today]) {
    const [openTime, closeTime] = scheduleWithWeekdays[today].split('-');
    if (currentTime < openTime) {
      // If current time is before today's opening time
      return `Closed - Opens at ${formatTime(openTime)}`;
    } else if (currentTime >= openTime && currentTime < closeTime) {
      // If current time is within today's open hours
      return `Open - Closes at ${formatTime(closeTime)}`;
    }
  }

  // If the store is closed, find the next day it opens
  for (let i = 1; i <= 7; i++) {
    const nextDay = now.plus({ days: i }).toFormat('cccc').toLowerCase();
    if (scheduleWithWeekdays[nextDay]) {
      const [nextOpenTime] = scheduleWithWeekdays[nextDay].split('-');
      return `Closed - Opens at ${formatTime(nextOpenTime)}`;
    }
  }

  return 'Schedule unavailable';
}
